/**
 * ModalSubTabs - Barra de abas (36px) no visual oficial do modal da O.S./Tarefas.
 * Fundo suave, ícone + texto, ativa em cor primária com traço inferior e contador opcional.
 * Reutilizada pela barra principal da O.S. e pelas subabas do Resumo.
 *
 * RELEASE: 2026-10-18 v1.0.0 - Extraído de ServiceOrderModalTabs (mesmas classes).
 */
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ModalSubTabItem<T extends string> { id: T; label: string; icon: LucideIcon; count?: number }

interface Props<T extends string> {
  items: ModalSubTabItem<T>[];
  active: T;
  onChange: (id: T) => void;
  className?: string;
}

export function ModalSubTabs<T extends string>({ items, active, onChange, className }: Props<T>) {
  return (
    <div className={cn('flex h-9 shrink-0 items-center overflow-x-auto border-b bg-muted/20 px-4', className)}>
      {items.map(({ id, label, icon: Icon, count }) => {
        const isActive = active === id;
        return (
          <button key={id} type="button" onClick={() => onChange(id)}
            className={cn('relative flex h-full shrink-0 items-center gap-1.5 px-3 text-xs font-medium transition-colors hover:text-foreground',
              isActive ? 'text-primary' : 'text-muted-foreground')}>
            <Icon className="h-3.5 w-3.5" />
            <span>{label}</span>
            {count !== undefined && count > 0 && (
              <span className={cn('flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px]',
                isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>{count}</span>
            )}
            {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t-full bg-primary" />}
          </button>
        );
      })}
    </div>
  );
}
